import type { ReactNode } from 'react';
import { IApiClient } from './ApiClient';
import { EventEmitter } from './helper/EventHelper';
import { ISiteServicesApiClient } from './SiteServicesApiClient';
import { AccountStore } from './store/AccountStore';
import { SupportCodeStore } from './store/SupportCodeStore';
import type { ButtonColor } from './UiTypes';

// Singleton App Context
let appContext: IPanelAppContext | undefined = undefined;

export function getAppContext(): IPanelAppContext {
    if (!appContext) throw new Error('Attempt to get uninitialized app context');
    return appContext;
}

// Non-throwing variant for callers that can safely no-op when the context is
// not yet initialised (e.g. WaitFor dispatching screen-reader announcements
// from isolated component tests that never call setAppContext).
export function tryGetAppContext(): IPanelAppContext | undefined {
    return appContext;
}

export function setAppContext(context: IPanelAppContext) {
    appContext = context;
}

export type Theme = 'Mammoth' | 'BinaryLane';

export type Notification = {
    color: string;
    title: string;
    body: JSX.Element;
    timeout?: number;
};

export type ProductFeature = 'PaymentFrequency' | 'PlanName' | 'ServiceLevel' | 'OperatingSystemSurchargeCredit';

export type ConfirmOptions = {
    title: string;
    message: ReactNode;
    warning?: ReactNode;
    confirmText?: string;
    cancelText?: string;
    confirmColor?: ButtonColor;
    acknowledgement?: {
        label: ReactNode;
    };

    // Optional async action bound to the confirm button. When present, the modal
    // stays open with a busy state while `perform` runs (every dismissal path is
    // disabled), then:
    //   - resolves → modal closes and `onSuccess` fires (if provided)
    //   - rejects  → modal stays open with `renderError(e)` shown inline; the
    //                customer can retry or cancel.
    // When omitted, `confirm()` resolves `true` immediately on confirm and the
    // caller runs the action itself (the original boolean-resolving contract).
    // `onSuccess`/`renderError` are nested here so they can't be set without
    // `perform` — the compiler rejects the half-configured case.
    asyncAction?: {
        perform: () => Promise<unknown>;
        // Fired after `perform` resolves successfully and the modal closes. Use for
        // non-tracked-action flows where the success signal is otherwise invisible.
        // For tracked-action flows leave undefined — the progress banner from
        // `setTrackedAction()` is the implicit signal.
        onSuccess?: () => void;
        // Renders the inline error shown inside the modal body when `perform`
        // rejects. Defaults to a generic "Something went wrong" message.
        renderError?: (error: unknown) => ReactNode;
    };
};

// mapped from PanelClientAppConfig.cs
export type AppConfig = {
    wwwSite: string;
    apiSite: string;
    csrfToken: string;
    // Since the possible values of the following are not synced with the Brand and ProductFeature types above, we use string.
    theme: string; // Should be queried with IPanelAppContext.isTheme
    enabledFeatures: string[]; // Should be queried with IPanelAppContext.IsEnabled
    supportEmail: string;
    supportSite: string;
    statusSite: string;
    companyName: string;
    brandName: string;
    productName: string;
};

export interface IPanelAppContext {
    config: AppConfig;
    customer: AccountStore;
    // Shared so that every surface showing the code picks up the new one after a password change.
    supportCode: SupportCodeStore;
    apiClient: IApiClient;
    siteApiClient: ISiteServicesApiClient;
    navigate: (url: string, replace?: boolean) => void;
    notify: EventEmitter<Notification>;
    // Polite screen-reader announcement channel. Fire a short message (e.g.
    // "Servers loaded") and the layout-level Announcer reads it via an
    // aria-live="polite" region. General purpose — not WaitFor-specific.
    announce: EventEmitter<string>;
    // the following use `this: void` so that they can be destructured eg `const { isEnabled, isBrand } = getAppContext()`
    isEnabled(this: void, feature: ProductFeature): boolean;
    isTheme(this: void, theme: Theme): boolean;
    showSearch: EventEmitter<boolean>;
    confirm(options: ConfirmOptions): Promise<boolean>;
    // error accepts `unknown` because error sources (catch blocks, FormStore.lastError,
    // LoadingTracker.lastError) are all typed as `unknown`. The implementation uses type
    // guards (isProblemDetails, isValidationProblemDetails) to format the display.
    //
    // Returns void rather than a Promise because when a second error arrives, the first
    // is replaced without the user dismissing it. An `await` would resolve without the
    // user ever seeing the original error — a footgun. If you need post-dismissal work,
    // route it through the component that knows about the error, not this global API.
    error(error: unknown, options?: ErrorModalOptions): void;
}

// Renamed from `ErrorOptions` to avoid collision with the DOM lib's built-in
// `interface ErrorOptions { cause?: unknown }` used by the `Error` constructor.
export type ErrorModalOptions = {
    title?: string;
    /** A human-readable summary of what failed (e.g. "Unable to load SSH keys"),
     *  shown as the lead line above the server's structured error detail. */
    errorSummary?: string;
};
